import { useEffect, useState } from "react";
import axiosInstance from "../../auth/axiosWithInterceptor";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../../lib/components/ui/card";
import { Button } from "../../lib/components/ui/button";
import { Input } from "../../lib/components/ui/input";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { useCart } from "../../features/cart/hooks/useCart";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/format";
import type { CartItem } from "@/types/catalog";

function CartSkeleton() {
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-24" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start justify-between gap-4 border-b pb-3">
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-2/5" />
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Skeleton className="h-9 w-20 rounded-md" />
                <Skeleton className="h-9 w-20 rounded-md" />
              </div>
            </div>
          ))}
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <div className="w-full space-y-2">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-12" />
            </div>
            <div className="flex justify-between border-t pt-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
          <Skeleton className="h-10 w-full rounded-md" />
        </CardFooter>
      </Card>
    </div>
  );
}

type Cart = {
  id: number;
  items: CartItem[];
};

type StoreSettings = {
  currency: string;
  tax_rate: number;
};

export default function CartPage() {
  // --- START EDIT: Add translation hooks for cart and common namespaces ---
  const { t } = useTranslation('cart');
  // --- END EDIT: Add translation hooks ---
  const [cart, setCart] = useState<Cart | null>(null);
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const { refresh } = useCart();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cartRes, settingsRes] = await Promise.all([
        axiosInstance.get("/cart", { withCredentials: true }),
        axiosInstance.get("/store/settings")
      ]);
      setCart(cartRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Error fetching cart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- START EDIT: Helper to find cart item by id after re-sync ---
  const findItemById = (cartData: Cart, itemId: number): CartItem | undefined => {
    return cartData?.items?.find((item: CartItem) => item.id === itemId);
  };

  const findItemByModelFileId = (cartData: Cart, targetModelFileId: number): CartItem | undefined => {
    return cartData?.items?.find((item: CartItem) => item.model_file_id === targetModelFileId);
  };
  // --- END EDIT: Helper functions ---

  // --- START EDIT: Re-sync strategy for quantity updates with 404 handling ---
  const handleQtyChange = async (itemId: number, quantity: number, modelFileId?: number | null) => {
    if (quantity < 1) return;
    
    try {
      const res = await axiosInstance.patch(
        `/cart/items/${itemId}`, 
        { model_file_id: 0, quantity }, 
        { withCredentials: true }
      );
      setCart(res.data);
      refresh();
    } catch (error: unknown) {
      // Handle 404: cart has changed, re-sync and retry
      if (axios.isAxiosError(error) && error?.response?.status === 404) {
        try {
          const cartRes = await axiosInstance.get("/cart", { withCredentials: true });
          setCart(cartRes.data);
          
          // Try to find the item - first by exact id, then by model_file_id for legacy items
          let currentItem = findItemById(cartRes.data, itemId);
          if (!currentItem && modelFileId) {
            currentItem = findItemByModelFileId(cartRes.data, modelFileId);
          }
          
          if (currentItem) {
            const retryRes = await axiosInstance.patch(
              `/cart/items/${currentItem.id}`,
              { model_file_id: 0, quantity },
              { withCredentials: true }
            );
            setCart(retryRes.data);
            refresh();
            toast.success(t('success.updated'));
          } else {
            toast.error(t('errors.updateFailed'));
            refresh();
          }
        } catch (retryError) {
          console.error("[Cart] Error during re-sync/retry:", retryError);
          toast.error(t('errors.updateFailed'));
        }
      } else {
        console.error("[Cart] Error updating quantity:", error);
        toast.error(t('errors.updateFailed'));
      }
    }
  };
  // --- END EDIT: Re-sync strategy for quantity updates ---

  // --- START EDIT: Re-sync strategy for item removal with 404 handling ---
  const handleRemove = async (itemId: number, modelFileId?: number | null) => {
    try {
      const res = await axiosInstance.delete(`/cart/items/${itemId}`, { withCredentials: true });
      setCart(res.data);
      refresh();
      toast.success(t('success.removed'));
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error?.response?.status === 404) {
        try {
          const cartRes = await axiosInstance.get("/cart", { withCredentials: true });
          setCart(cartRes.data);
          
          let currentItem = findItemById(cartRes.data, itemId);
          if (!currentItem && modelFileId) {
            currentItem = findItemByModelFileId(cartRes.data, modelFileId);
          }
          
          if (currentItem) {
            const retryRes = await axiosInstance.delete(`/cart/items/${currentItem.id}`, { withCredentials: true });
            setCart(retryRes.data);
            refresh();
            toast.success(t('success.removed'));
          } else {
            toast.info(t('errors.removeFailed'));
            refresh();
          }
        } catch (retryError) {
          console.error("[Cart] Error during re-sync/retry:", retryError);
          toast.error(t('errors.removeFailed'));
        }
      } else {
        console.error("[Cart] Error removing item:", error);
        toast.error(t('errors.removeFailed'));
      }
    }
  };
  // --- END EDIT: Re-sync strategy for item removal ---

  const calculateTotals = () => {
    if (!cart || !settings) return { subtotal: 0, vatOnItems: 0, total: 0 };
    
    const subtotal = cart.items.reduce((acc, it) => acc + it.subtotal, 0);
    const vatOnItems = subtotal * settings.tax_rate;
    const total = subtotal + vatOnItems;
    
    return { subtotal, vatOnItems, total };
  };

  if (loading) return <CartSkeleton />;


  const totals = calculateTotals();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          {/* --- START EDIT: Use translation for title --- */}
          <CardTitle>{t('title')}</CardTitle>
          {/* --- END EDIT: Use translation for title --- */}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* --- START EDIT: Use translation for empty cart message --- */}
          {!cart?.items?.length && <div>{t('empty')}</div>}
          {/* --- END EDIT: Use translation for empty cart message --- */}
          {cart?.items?.map((it) => (
            <div key={it.id} className="flex items-start justify-between gap-4 border-b pb-3">
              <div className="min-w-0 flex-1">
                {/* MODEL_FILE item */}
                {it.item_type === "MODEL_FILE" && (
                  <>
                    <div className="font-medium">{it.model_file_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {t('price')}: {formatCurrency(it.unit_price)} (excl. {t('totals.tax')})
                    </div>
                    {(it.layer_thickness != null || it.infill_percentage != null || it.infill_geometry != null) && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {[
                          it.layer_thickness != null ? `${it.layer_thickness} mm` : null,
                          it.infill_percentage != null ? `${it.infill_percentage}%` : null,
                          it.infill_geometry || null
                        ].filter(Boolean).join(' · ') || '—'}
                      </div>
                    )}
                    {it.quoted_roll && (
                      <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
                        <span
                          className="inline-block size-3 rounded"
                          style={{ backgroundColor: it.quoted_roll.hex_code ?? "#cccccc" }}
                          aria-label={it.quoted_roll.color ?? "color"}
                          title={it.quoted_roll.color ?? "Color"}
                        />
                        <span className="truncate">
                          {t('material')}: {it.quoted_roll.material ?? "Material"} {it.quoted_roll.color ? `— ${it.quoted_roll.color}` : ""}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* PRODUCT item */}
                {it.item_type === "PRODUCT" && (
                  <>
                    <div className="flex items-start gap-3">
                      {it.item_image_url && (
                        <img
                          src={it.item_image_url}
                          alt={it.item_name ?? "Product"}
                          className="h-12 w-12 rounded object-cover border border-border shrink-0"
                        />
                      )}
                      <div>
                        <div className="font-medium">{it.item_name}</div>
                        {it.product_code && (
                          <div className="text-xs text-muted-foreground">
                            #{it.product_code}
                          </div>
                        )}
                        <div className="text-sm text-muted-foreground">
                          {t('price')}: {formatCurrency(it.unit_price)} (excl. {t('totals.tax')})
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* RETAIL_FILAMENT item */}
                {it.item_type === "RETAIL_FILAMENT" && (
                  <>
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-0.5 shrink-0 inline-block size-4 rounded-full border border-border"
                        style={{ backgroundColor: it.filament_color_hex ?? "#cccccc" }}
                        aria-label={it.filament_color_name ?? "color"}
                        title={it.filament_color_name ?? "Color"}
                      />
                      <div>
                        <div className="font-medium">
                          {[it.filament_brand, it.filament_material, it.filament_color_name]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {it.filament_weight_grams && (
                          <div className="text-xs text-muted-foreground">
                            {it.filament_weight_grams}g
                          </div>
                        )}
                        <div className="text-sm text-muted-foreground">
                          {t('price')}: {formatCurrency(it.unit_price)} (excl. {t('totals.tax')})
                          {it.discount_percent != null && it.discount_percent > 0 && (
                            <span className="ml-1 text-xs text-green-600 dark:text-green-400">
                              ({it.discount_percent}% off)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="mt-1 text-xs text-muted-foreground">
                  {t('totals.subtotal')}: {formatCurrency(it.subtotal)} (excl. {t('totals.tax')})
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number"
                  min={1}
                  value={it.quantity}
                  onChange={(e) => handleQtyChange(it.id, parseInt(e.target.value || "1", 10), it.model_file_id)}
                  className="w-20"
                />
                <Button variant="destructive" onClick={() => handleRemove(it.id, it.model_file_id)}>{t('remove')}</Button>
              </div>
            </div>
          ))}
        </CardContent>
        
        {(cart?.items?.length ?? 0) > 0 && (
          <CardFooter className="flex flex-col space-y-2">
            <div className="w-full space-y-1 text-right">
              {/* --- START EDIT: Use translations and formatCurrency for totals --- */}
              <div className="flex justify-between">
                <span>{t('totals.subtotal')} (excl. {t('totals.tax')}):</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{t('totals.tax')} ({((settings?.tax_rate || 0.23) * 100).toFixed(1)}%):</span>
                <span>{formatCurrency(totals.vatOnItems)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>{t('totals.total')} (incl. {t('totals.tax')}):</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {t('totals.shipping')} calculated at checkout
              </p>
              {/* --- END EDIT: Use translations and formatCurrency for totals --- */}
            </div>
            {/* --- START EDIT: Use translation for checkout button --- */}
            <Button asChild className="w-full">
              <Link to="/checkout">{t('proceedToCheckout')}</Link>
            </Button>
            {/* --- END EDIT: Use translation for checkout button --- */}
          </CardFooter>
        )}
      </Card>
    </div>
  );
}